/**
 * Módulo de almacenamiento de autenticación — gestión de token JWT y estado de sesión en localStorage
 */

const TOKEN_KEY = 'token';
const USER_TYPE_KEY = 'userType';
const AUTH_KEY = 'isAuthenticated';
const USER_EMAIL_KEY = 'userEmail';
const USER_NAME_KEY = 'userName';
const USER_ROL_KEY = 'userRol';
const REMEMBERED_EMAIL_KEY = 'rememberedEmail';

/**
 * Crea el módulo de almacenamiento de autenticación
 *
 * Implementado con closures (sin `this`) para garantizar que los métodos que tienen
 * referencias cruzadas internas (isAdmin, isAuthenticated, getAuthHeaders) funcionen
 * independientemente del contexto de llamada.
 *
 * @returns {Object} Métodos de gestión de token y estado de sesión
 */
export function createAuthStorage() {
  /**
   * Obtiene el token JWT almacenado
   * @returns {string|null}
   */
  function getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Almacena el token JWT
   * @param {string} token - Token JWT a almacenar
   */
  function setToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
  }

  /**
   * Elimina el token y tipo de usuario
   */
  function removeToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_TYPE_KEY);
  }

  /**
   * Verifica si el token JWT ha expirado
   * @param {string} token - Token JWT a verificar
   * @returns {boolean} True si el token ha expirado o es inválido
   */
  function isTokenExpired(token) {
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  }

  /**
   * Obtiene el código del rol del usuario autenticado
   * @returns {string|null} Código del rol (ej: 'ADMIN', 'BASICO') o null
   */
  function getUserRol() {
    return localStorage.getItem(USER_ROL_KEY);
  }

  /**
   * Obtiene el email del usuario autenticado desde localStorage
   * @returns {string|null}
   */
  function getUserEmail() {
    return localStorage.getItem(USER_EMAIL_KEY);
  }

  /**
   * Obtiene el email recordado para login
   * @returns {string}
   */
  function getRememberedEmail() {
    return localStorage.getItem(REMEMBERED_EMAIL_KEY) || '';
  }

  /**
   * Almacena el email para recordar en login
   * @param {string} email - Email del usuario a recordar
   */
  function setRememberedEmail(email) {
    localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
  }

  /**
   * Elimina el email recordado
   */
  function removeRememberedEmail() {
    localStorage.removeItem(REMEMBERED_EMAIL_KEY);
  }

  /**
   * Establece el estado de autenticación
   * @param {string} userEmail - Email del usuario autenticado
   * @param {string} [userName] - Nombre del usuario
   * @param {string} [userRol] - Código del rol (ej: 'ADMIN', 'BASICO')
   */
  function setAuthState(userEmail, userName, userRol) {
    localStorage.setItem(AUTH_KEY, 'true');
    localStorage.setItem(USER_EMAIL_KEY, userEmail);
    if (userName) localStorage.setItem(USER_NAME_KEY, userName);
    if (userRol) localStorage.setItem(USER_ROL_KEY, userRol);
  }

  /**
   * Limpia el estado de autenticación
   */
  function clearAuthState() {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(USER_EMAIL_KEY);
    localStorage.removeItem(USER_NAME_KEY);
    localStorage.removeItem(USER_ROL_KEY);
  }

  /**
   * Verifica si el usuario autenticado es administrador
   * @returns {boolean}
   */
  function isAdmin() {
    return getUserRol() === 'ADMIN';
  }

  /**
   * Verifica si el usuario está autenticado
   * @returns {boolean}
   */
  function isAuthenticated() {
    return localStorage.getItem(AUTH_KEY) === 'true' && !!getToken();
  }

  /**
   * Obtiene headers de autorización con Bearer token
   * @returns {Object}
   * @throws {Error} Si no hay token o está expirado
   */
  function getAuthHeaders() {
    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    if (isTokenExpired(token)) {
      removeToken();
      throw new Error('Authentication token has expired');
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  return {
    getToken,
    setToken,
    removeToken,
    isTokenExpired,
    getAuthHeaders,
    isAdmin,
    isAuthenticated,
    getUserRol,
    getUserEmail,
    setAuthState,
    clearAuthState,
    getRememberedEmail,
    setRememberedEmail,
    removeRememberedEmail
  };
}
